<script setup>
import { useI18n } from 'vue-i18n';
import { computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { SAML_ATTRIBUTES } from '../../helpers/constants';

import Input from 'next/input/Input.vue';
import ComboBox from 'next/combobox/ComboBox.vue';
import { samlAssertionAttributeName } from '../../helpers/rulesHelper';

const props = defineProps({
  existingAttributeKeys: {
    type: Array,
    default: () => [],
  },
  initialKey: {
    type: String,
    default: '',
  },
  initialValue: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['submit']);
const { t } = useI18n();

const attributesOptions = SAML_ATTRIBUTES.map(value => {
  return {
    value,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(
      `WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.FORM.VALUE.OPTIONS.${value.toUpperCase()}`
    ),
  };
});

const state = reactive({
  key: props.initialKey,
  value: props.initialValue,
});

const rules = {
  key: {
    required,
    samlAssertionAttributeName,
    unique: value => {
      if (!value || value === props.initialKey) return true;
      return !props.existingAttributeKeys.includes(value);
    },
  },
  value: { required },
};

const v$ = useVuelidate(rules, state);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.FORM';
  const [error] = v$.value[field].$errors;
  return error
    ? // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      t(`${baseKey}.${errorKey}.ERRORS.${error.$validator.toUpperCase()}`)
    : '';
};

const formErrors = computed(() => ({
  key: getErrorMessage('key', 'KEY'),
  value: getErrorMessage('value', 'VALUE'),
}));

const resetState = () => {
  state.key = props.initialKey;
  state.value = props.initialValue;
};

const validate = () => {
  return v$.value.$validate();
};

const handleSubmit = async () => {
  const isFormValid = await validate();
  if (!isFormValid) return;

  emit('submit', state);
};

defineExpose({ state, validate, resetState });
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.key"
      :label="t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.FORM.KEY.LABEL')"
      :disabled="!!props.initialKey"
      :message="formErrors.key"
      :message-type="v$.key.$error ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="value" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.FORM.VALUE.LABEL') }}
      </label>
      <ComboBox
        v-model="state.value"
        :options="attributesOptions"
        :has-error="v$.value.$error"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        :class="{
          '[&>div>button]:border [&>div>button]:border-n-ruby-8 [&>div>button:not(.focused)]:hover:!border-n-ruby-9':
            v$.value.$error,
        }"
        @change="v$.value.$touch"
      />
    </div>
  </form>
</template>
